// ✅ src/pages/OrderManagement/RulesPrint.jsx
// Print-only page (NO sidebar / NO theme / NO scroll)
// ASPX equivalent: Rulesreportprint.aspx
//
// API used:
//   GET http://nissi-india.com/api/rules/view?contractId=1
//
// Usage:
//   /OrderManagement/RulesPrint?contractId=1

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaPrint } from "react-icons/fa";

const API = "http://nissi-india.com/api/rules";

const toInt = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

export default function RulesPrint() {
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const contractId = useMemo(
    () => toInt(qs.get("contractId") || qs.get("contractid")),
    [qs]
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/view?contractId=${contractId}`);
      if (res.data?.success) {
        setRows(res.data?.rows || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!contractId) {
      setLoading(false);
      setRows([]);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const fmtDateTime = (d) => {
    try {
      return new Date(d).toLocaleString();
    } catch {
      return "";
    }
  };

  return (
    <div className="p-6 text-black bg-white print:p-0">
      {/* ================= HEADER ================= */}
      <div className="border-2 border-black mb-4">
        <div className="text-center py-3 border-b border-black">
          <h1 className="text-lg font-bold text-red-600">Nissi Infotech</h1>
          <div className="text-sm">Nissi</div>
          <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>
          <div className="text-xs">
            3/42-12, Nagappa colony, kavundampalayam – 641030, Tamil Nadu, India
          </div>
          <div className="text-xs">
            Phone: 9003744644 | Email: infraconsoft-implementation@nissiinfotech.in
          </div>
        </div>

        <div className="text-center py-2 border-b border-black font-bold">
          DO&apos;S / DON&apos;TS REPORT
        </div>

        <div className="px-3 py-2 text-sm">
          <b>Contract ID :</b> {contractId || "-"}
        </div>
      </div>

      {/* ================= ACTION BAR (HIDDEN IN PRINT) ================= */}
      <div className="flex justify-end mb-4 print:hidden">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
        >
          <FaPrint />
          Print
        </button>
      </div>

      {/* ================= TABLE ================= */}
      <div className="border border-black">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-black">
              <th className="border-r border-black p-2 w-[60px]">S.No</th>
              <th className="border-r border-black p-2 w-[220px]">Title</th>
              <th className="border-r border-black p-2">Do&apos;s</th>
              <th className="border-r border-black p-2 text-red-700">
                Don&apos;t Do&apos;s
              </th>
              <th className="p-2 w-[160px]">Created On</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center">
                  No Records Found
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.fld_rulesid || i} className="border-b border-black align-top">
                  <td className="border-r border-black p-2 text-center">{i + 1}</td>
                  <td className="border-r border-black p-2 font-semibold">
                    {r.fld_Title}
                  </td>
                  <td className="border-r border-black p-2 whitespace-pre-wrap">
                    {r.fld_Do}
                  </td>
                  <td className="border-r border-black p-2 whitespace-pre-wrap text-red-700">
                    {r.fld_Dont}
                  </td>
                  <td className="p-2 text-center">
                    {r.Createdon ? fmtDateTime(r.Createdon) : ""}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="mt-6 text-xs text-right">
        Printed on : {new Date().toLocaleString()}
      </div>
    </div>
  );
}
